import Layout from '@/components/Layout/Vanilla';
import Button from '@/components/Button';
import Hero from '@/components/Hero';
import SectionHero from '@/components/SectionHero';
import IndustryList from '@/components/IndustryList';
import IconContentBlock from '@/components/IconContentBlock';
import ContentBlock from '@/components/ContentBlock';
import TestimonialCard from '@/components/TestimonialCard';
import USPGrid from '@/components/USPGrid';
import CardGrid from '@/components/CardGrid';
import { DefaultLogoReel } from '@/components/LogoReel';
import Section from '@/components/Section';
import { fetchPage, gql } from '@/lib/graphcms';

const features = [
  {
    icon: 'CHAT',
    title: 'Support SLA',
    description:
      'All enterprise plans come with customised service SLAs, including 24/7 support, a dedicated account manager, and solution architect.',
  },
  {
    icon: 'WRENCH',
    title: 'Performance SLA',
    description:
      'Enterprise plans come with an uptime SLA of up to 99.9%, and the option of a dedicated infrastructure hosted across any AWS region if needed.',
  },
  {
    icon: 'GLOBE',
    title: 'Partner Network',
    description:
      'To accelerate complex projects, we can connect you to implementation experts within our partner network.',
  },
];

export default function EnterpriseBase({ page, caseStudies, resources }) {
  const { usps, contentBlocks } = page;

  return (
    <Layout seo={page}>
      <Section>
        <Hero {...page} alignment="left">
          <Button
            href="/contact"
            appearance="outline"
            colour="pink"
            size="large"
          >
            Contact Sales &#8594;
          </Button>
        </Hero>
      </Section>
      <DefaultLogoReel />
      <Section>
        <SectionHero
          title="Why GraphCMS"
          description="GraphCMS delivers on enterprise expectations, enabling teams to successfully take digital projects to market globally"
        />

        <div className="md:flex md:flex-wrap -mx-6">
          {features.map(IconContentBlock)}
        </div>

        <USPGrid title={false} items={usps} />
      </Section>
      <Section>
        <SectionHero
          title="Overcome the monolith"
          description="Break down your legacy systems, work with best-of-breed microservices, and deliver a genuinely world-class digital experience to your customers"
        />
        <IndustryList entries={contentBlocks} Component={ContentBlock} />
      </Section>
      <Section colour="light">
        <SectionHero
          title="Learn more about how companies across industries use GraphCMS"
          description="We work with forward thinking teams of all sizes, to deliver digital projects globally"
          ctas={[
            { href: '/case-studies', label: 'View all customer case studies' },
          ]}
        />
        <IndustryList entries={caseStudies} Component={TestimonialCard} />
      </Section>
      <Section>
        <SectionHero
          title="Understand how GraphCMS can help your team bring projects to market"
          description="Get up to speed with our real-world studies and experiences to incorporate GraphCMS into your organisation"
          ctas={[{ href: '/resources', label: 'Explore GraphCMS resources' }]}
        />
        <CardGrid entries={resources} linkSubpath="resources" />
      </Section>
    </Layout>
  );
}

export const getStaticProps = async ({ preview = false }) => {
  const { page, resources, caseStudies } =
    (await fetchPage(
      gql`
        fragment enterpriseAuthorData on Author {
          name
          title
          bio
          photo {
            ...assetDataXL
          }
        }

        fragment enterpriseResourceData on Resource {
          id
          slug
          title
          subTitle
          coverImage {
            ...assetDataXL
          }
          speakers {
            ...enterpriseAuthorData
          }
          content
          externalUrl
          location
          starts
          type
        }

        fragment enterpriseCaseStudyData on CaseStudy {
          id
          slug
          title
          subTitle
          coverImage {
            id
            url
            width
            height
            fileName
            alt
          }
          excerpt
          content
          externalUrl
          company {
            name
            url
            logo {
              ...assetDataXL
            }
          }
          customer {
            ...enterpriseAuthorData
          }
        }

        query contentManagementPageQuery {
          page(where: { slug: "enterprise-headless-cms" }) {
            ...pageData # Fragment is inside the method
          }
          resources(
            where: { type_in: [EBOOK, WEBINAR] }
            first: 2
            orderBy: createdAt_DESC
          ) {
            ...enterpriseResourceData
          }
          caseStudies(
            where: {
              id_in: ["ckgs1j1b400rw0a044fk0hpys", "ck5nsgxtt0avy0b662swgflk5"]
            }
          ) {
            ...enterpriseCaseStudyData
          }
        }
      `,
      {
        preview,
      },
    )) || [];

  return {
    props: {
      preview,
      page,
      resources,
      caseStudies,
    },
    revalidate: 3600,
  };
};
